type ConfirmationValidationFailure = (path: string, message: string) => never;

const CONFIRMATION_KEYS = new Set([
  'title',
  'description',
  'confirmLabel',
  'cancelLabel',
  'affectedEntities',
]);
const ENTITY_KEYS = new Set(['id', 'label', 'outcome']);

function record(
  value: unknown,
  path: string,
  fail: ConfirmationValidationFailure,
): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)
    || Object.getPrototypeOf(value) !== Object.prototype) {
    return fail(path, 'must be a plain object');
  }
  return value as Record<string, unknown>;
}

function exact(
  value: Record<string, unknown>,
  allowed: ReadonlySet<string>,
  path: string,
  fail: ConfirmationValidationFailure,
): void {
  const unknown = Object.keys(value).find((key) => !allowed.has(key));
  if (unknown) fail(`${path}.${unknown}`, 'is not part of the sealed confirmation contract');
}

function text(value: unknown, path: string, fail: ConfirmationValidationFailure): string {
  if (typeof value !== 'string' || value.trim() === '') {
    return fail(path, 'must be non-empty plain text');
  }
  return value;
}

export function validateFmActionConfirmation(
  value: unknown,
  path: string,
  fail: ConfirmationValidationFailure,
): void {
  const confirmation = record(value, path, fail);
  exact(confirmation, CONFIRMATION_KEYS, path, fail);
  ['title', 'description', 'confirmLabel', 'cancelLabel'].forEach((key) =>
    text(confirmation[key], `${path}.${key}`, fail));
  if (!Array.isArray(confirmation.affectedEntities)
    || confirmation.affectedEntities.length === 0) {
    fail(`${path}.affectedEntities`, 'must contain at least one affected entity');
  }
  const identities = new Set<string>();
  confirmation.affectedEntities.forEach((value, index) => {
    const entityPath = `${path}.affectedEntities[${index}]`;
    const entity = record(value, entityPath, fail);
    exact(entity, ENTITY_KEYS, entityPath, fail);
    const id = text(entity.id, `${entityPath}.id`, fail);
    text(entity.label, `${entityPath}.label`, fail);
    text(entity.outcome, `${entityPath}.outcome`, fail);
    if (identities.has(id)) fail(`${entityPath}.id`, 'must be unique');
    identities.add(id);
  });
}
